import React, { useEffect, useState, useMemo } from 'react';
import {
  Container,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  CircularProgress,
  Alert,
  TextField,
  TableContainer,
  Paper,
  Grid,
  Box,
  Stack,
  Chip,
} from '@mui/material';
import axios from 'axios';
import { useAuth } from '../../AuthContext';
import Header1 from '../Header/Header1';
import Footer2 from '../Footer/Footer2';

const API_URL = process.env.REACT_APP_API_URL;

const AdminSalesPartnerDashboard = () => {
  const { userId } = useAuth();
  const [data, setData] = useState({ individuals: [], companies: [] });
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [userMap, setUserMap] = useState({}); // map userId -> { name, surname, image }
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch user info
  const fetchUser = async (id) => {
    try {
      const res = await axios.post(`${API_URL}/api/user/`, { userId: id });
      return res.data;
    } catch (err) {
      console.error('Error fetching user info for:', id, err);
      return null;
    }
  };

  // Fetch all data and user info
  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/sales-partner/all/`);
      const respData = res.data;
      setData(respData);

      // Collect unique user IDs from both applicants and reviewers
      const ids = new Set();
      ['individuals', 'companies'].forEach((type) => {
        respData[type].forEach((row) => {
          if (row.user_id) ids.add(row.user_id);
          if (row.reviewed_by) ids.add(row.reviewed_by);
        });
      });

      const map = {};
      await Promise.all(
        [...ids].map(async (id) => {
          const u = await fetchUser(id);
          if (u) {
            map[id] = u;
          }
        })
      );
      setUserMap(map);
    } catch (err) {
      console.error('Error fetching sales partner data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (user_id, action) => {
    try {
      const res = await axios.patch(`${API_URL}/api/sales-partner/${action}/`, {
        user_id,
        reviewed_by: userId,
      });
      setMsg(res.data.message);
      await fetchAll();
    } catch (err) {
      console.error(err);
      setMsg('Action failed');
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const getFullName = (id) => {
    const u = userMap[id];
    return u ? `${u.name || ''} ${u.surname || ''}`.trim() : 'Loading...';
  };

  // Filtered data based on search
  const filteredData = useMemo(() => {
    const sq = searchQuery.toLowerCase().trim();
    if (!sq) return data;

    const filterRows = (rows) =>
      rows.filter((row) => {
        const applicantName = getFullName(row.user_id).toLowerCase();
        const status = (row.status || '').toLowerCase();
        const idField = row.govt_id
          ? row.govt_id.toLowerCase()
          : row.gst_no
          ? row.gst_no.toLowerCase()
          : '';
        return (
          applicantName.includes(sq) || status.includes(sq) || idField.includes(sq)
        );
      });

    return {
      individuals: filterRows(data.individuals),
      companies: filterRows(data.companies),
    };
  }, [searchQuery, data, userMap]);

  // Calculate counts for individuals and companies (based on filtered data)
  const counts = useMemo(() => {
    const countCategory = (arr) => {
      const total = arr.length;
      const approved = arr.filter((r) => r.status === 'approved').length;
      const notReviewed = arr.filter((r) => !r.reviewed_by).length;
      const revoked = arr.filter((r) => r.status === 'revoked').length;
      return { total, approved, notReviewed, revoked };
    };
    return {
      individuals: countCategory(filteredData.individuals),
      companies: countCategory(filteredData.companies),
    };
  }, [filteredData]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header1 />

      <Container
        maxWidth="lg"
        sx={{
          mt: 4,
          mb: 5,
          display: 'flex',
          flexDirection: 'column',
          minHeight: { xs: 'auto', md: '80vh' },
        }}
      >
        <Typography
          variant="h3"
          component="h1"
          align="center"
          fontSize={{ xs: '2rem', sm: '2.5rem', md: '3rem' }}
          sx={{
            backgroundImage: 'linear-gradient(45deg, #00bfff, #ff00ff)',
            mt: { xs: 6, sm: 10 },
            letterSpacing: 7,
            WebkitBackgroundClip: 'text',
            color: 'transparent',
          }}
          gutterBottom
        >
          Sales Partner Requests
        </Typography>

        {/* Counts summary */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Individuals
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
            <Chip label={`Total: ${counts.individuals.total}`} color="primary" />
            <Chip label={`Approved: ${counts.individuals.approved}`} color="success" />
            <Chip label={`Not Reviewed: ${counts.individuals.notReviewed}`} color="warning" />
            <Chip label={`Revoked: ${counts.individuals.revoked}`} color="error" />
          </Stack>

          <Typography variant="h6" sx={{ mb: 1 }}>
            Companies
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip label={`Total: ${counts.companies.total}`} color="primary" />
            <Chip label={`Approved: ${counts.companies.approved}`} color="success" />
            <Chip label={`Not Reviewed: ${counts.companies.notReviewed}`} color="warning" />
            <Chip label={`Revoked: ${counts.companies.revoked}`} color="error" />
          </Stack>
        </Box>

        <Grid container spacing={2} justifyContent="center" sx={{ mb: 3 }}>
          <Grid item xs={12} sm={8} md={6}>
            <TextField
              label="Search by name, ID, status..."
              variant="outlined"
              fullWidth
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Grid>
        </Grid>

        {msg && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {msg}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, flexGrow: 1 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ flexGrow: 1 }}>
            {['individuals', 'companies'].map((type) => (
              <Box key={type} sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ mt: 3, mb: 1, textTransform: 'capitalize' }}>
                  {type}
                </Typography>
                <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Applicant Name</TableCell>
                        {type === 'companies' && <TableCell>Company Name</TableCell>}
                        {type === 'companies' && <TableCell>Company Address</TableCell>}
                        <TableCell>{type === 'individuals' ? 'Govt ID' : 'GST No'}</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Reviewed By</TableCell>
                        <TableCell align="center">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredData[type].length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={type === 'companies' ? 7 : 5} align="center">
                            No {type} found.
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredData[type].map((row) => (
                          <TableRow key={row.id}>
                            <TableCell>{getFullName(row.user_id)}</TableCell>
                            {type === 'companies' && <TableCell>{row.company_name || '-'}</TableCell>}
                            {type === 'companies' && <TableCell>{row.company_address || '-'}</TableCell>}
                            <TableCell>{type === 'individuals' ? row.govt_id : row.gst_no}</TableCell>
                            <TableCell>{row.status}</TableCell>
                            <TableCell>
                              {row.reviewed_by ? getFullName(row.reviewed_by) : 'Not Reviewed'}
                            </TableCell>
                            <TableCell align="center">
                              {row.status === 'approved' ? (
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  onClick={() => handleAction(row.user_id, 'revoke')}
                                >
                                  Revoke
                                </Button>
                              ) : (
                                <Button
                                  variant="contained"
                                  color="primary"
                                  size="small"
                                  onClick={() => handleAction(row.user_id, 'approve')}
                                >
                                  Approve
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))}
          </Box>
        )}
      </Container>

      <Footer2 />
    </Box>
  );
};

export default AdminSalesPartnerDashboard;
